/* global window */

import get from 'lodash/get';
import find from 'lodash/find';
import isMobileJS from 'ismobilejs';
import loadJS from 'load-js';

import Embed from 'video-js-core/src/assets/Embed';
import { isBrowserEdge } from './utils';

/**
 * A Nowcast embedded asset.
 */
export default class NowcastEmbed extends Embed {
  /**
   * Create a video embed asset.
   * @param {string} id - Embed ID.
   * @param {Object} settings - Settings object.
   * @param {Object} defaultAttributes - optional attributes.
   */
  constructor(id, settings, defaultAttributes) {
    super(id, settings, defaultAttributes);
    this.playerIsSticky = false;
    this.playerSkipExitEvent = false;
    this.playerSkipEnterEvent = false;
    this.playerAdTagUrl = null;
    this.playerIndex = 1;
    this.liveStreamSource = null;
    this.daiTracker = null;
    this.adStartEvent = 'inViewPlay';

    this.setEventData();
  }

  /**
   * Sets attribute data passed to video events.
   */
  setEventData() {
    this.attributes.id = this.id;
    this.attributes.autoplay = this.autoplay;
    this.attributes.ads_disabled = !this.adsEnabled;
    this.attributes.mute = this.muted;
    this.attributes.role = 3; // lead video
    this.attributes.created_at = Date.now();

    this.attributes.media = {
      id: this.id,
      type: this.type,
      preview_image: this.poster,
      title: this.title,
      source: {
        title: this.videoSource,
        categories: [],
        url: get(window, 'HRST.site.domain'),
      },
      categories: [],
    };
  }

  /**
   * Sets live stream source.
   * @param {string} liveStreamSource - live stream source
   */
  setLiveStreamSource(liveStreamSource) {
    this.liveStreamSource = liveStreamSource;
  }

  /**
   * Sets DAI tracker.
   * @param {Object} daiTracker - DAI tracker
   */
  setDaiTracker(daiTracker) {
    this.daiTracker = daiTracker;
  }

  /**
   * @return {boolean} Whether ads are enabled.
   */
  get adsEnabled() {
    return true;
  }

  /**
   * @return {boolean} Whether player can be sticky.
   */
  get canBeSticky() {
    return false;
  }

  /**
   * @returns {boolean} Whether embed contains captions.
   */
  get hasCaptions() {
    return false;
  }

  /**
   * Returns source Ad Tag URL.
   * @returns {string} Ad Tag URL
   */
  get adTagUrl() {
    return this.playerAdTagUrl;
  }

  /**
   * Sets source Ad Tag URL.
   * @param {string} adTagUrl - Ad Tag URL
   */
  setAdTagUrl(adTagUrl) {
    this.playerAdTagUrl = adTagUrl;
  }

  /**
   * Returns source sticky state.
   * @returns {boolean} playerIsSticky
   */
  get isSticky() {
    return this.playerIsSticky;
  }

  /**
   * Sets source sticky state.
   * @param {boolean} isSticky
   */
  setIsSticky(isSticky) {
    this.playerIsSticky = isSticky;
  }

  /**
  * Returns source skip next inview exit event.
  * @returns {boolean} skipNextExit
  */
  get skipExitEvent() {
    return this.playerSkipExitEvent;
  }

  /**
  * Sets source skip exit event state.
  * @param {boolean} skipExit
  */
  setSkipExitEvent(skipExitEvent) {
    this.playerSkipExitEvent = skipExitEvent;
  }
  /**
  * Returns source skip next inview enter event.
  * @returns {boolean} skipNextEnter
  */
  get skipEnterEvent() {
    return this.playerSkipEnterEvent;
  }
  /**
  * Sets source skip enter event state.
  * @param {boolean} skipEnterEvent
  */
  setSkipEnterEvent(skipEnterEvent) {
    this.playerSkipEnterEvent = skipEnterEvent;
  }

  setAdStartEvent(adStartEvent) {
    this.adStartEvent = adStartEvent;
  }

  /**
   * Whether this source type requires recommendations.
   * @returns {boolean}
   */
  get requiresRecommendations() {
    return false;
  }

  /**
   * Returns source playlist index.
   * @returns {integer} playlist index
   */
  get index() {
    return this.playerIndex;
  }

  /**
   * Sets source playlist index.
   * @param {integer} index playlist index
   */
  setIndex(index) {
    this.playerIndex = index;
  }

  /**
   * @returns {string} title of the asset
   */
  get title() {
    const nowcast = find(window.hearstLumiereRecs, {
      id: this.id,
    });

    const title = get(nowcast, 'playlist.0.title');

    return title;
  }

  /**
   * @returns {string} description of the video asset
   */
  get description() {
    const nowcast = find(window.hearstLumiereRecs, {
      id: this.id,
    });

    const description = get(nowcast, 'playlist.0.title');

    return description;
  }

  /**
   * @returns {string} preview image URL
   */
  get poster() {
    const nowcast = find(window.hearstLumiereRecs, {
      id: this.id,
    });

    const poster = get(nowcast, 'playlist.0.poster');

    return poster;
  }

  /**
   * @returns {string} Aspect ratio setting. Defaults to false for fluid layouts.
   */
  get aspectRatio() {
    return '16:9';
  }

  /**
   * @return {boolean} Autoplay embed setting.
   */
  get autoplay() {
    return true;
  }

  /**
   * Returns the muted setting.  If we are autoplaying, we should always
   * be muted.
   * @returns {boolean} muted embed setting
   */
  get muted() {
    return false;
  }

  /**
   * @return {string} For single video embeds, the video ID.
   */
  get videoId() {
    return this.id;
  }

  /**
   * @return {string} Video source name, such as 'KTVU'.
   */
  get videoSource() {
    const nowcast = find(window.hearstLumiereRecs, {
      id: this.id,
    });

    const source = get(nowcast, 'playlist.0.source');

    return source;
  }

  /**
   * Retrieve the type of embed (video or playlist).
   */
  get mediaType() {
    return 'video';
  }

  /**
   * Whether this embed represents a playlist or not.
   *
   * @returns {boolean} True if a playlist, or false otherwise.
   */
  get isPlaylist() {
    return false;
  }

  /**
   * @returns {string} M3U8 URL.
   */
  get m3u8Url() {
    if (this.liveStreamSource) {
      return this.liveStreamSource;
    }

    const nowcast = find(window.hearstLumiereRecs, {
      id: this.id,
    });

    const url = get(nowcast, 'playlist.0.url');

    return url;
  }

  /**
   * @returns {string} mp4 URL.
   */
  get mp4Url() {
    return null;
  }

  /**
   * @returns {string} DAI asset ID.
   */
  get daiAssetId() {
    return this.attributes.daiAssetId;
  }

  /**
   * @returns {boolean} id DAI enabled.
   */
  get isDaiEnabled() {
    return this.attributes.isDaiEnabled;
  }

  /**
   * Loads DAI scripts and requests live stream in DAI mode.
   * Resolves to default in VOD mode.
   */
  load() {
    return new Promise(resolve => {
      loadJS(this.settings.nowcastScripts)
        .then(() => {
          if (this.daiTracker) {
            this.daiTracker.setupStreamManager();
            this.daiTracker.setupCueEvents();
            resolve(this.daiTracker.requestLiveStream());
          }
          resolve();
        });
    });
  }

  /**
   * @returns {integer} stream type.
   */
  get type() {
    const nowcast = find(window.hearstLumiereRecs, {
      id: this.id,
    });

    const type = get(nowcast, 'playlist.0.type');

    return type;
  }

  /**
   * @returns {boolean} is live stream.
   */
  get isLiveStream() {
    return this.type === 2 || this.type === 888;
  }

  /**
   * @return {boolean} has anime.
   */
  get hasAnime() {
    return false;
  }

  /**
   * @returns {Object} tech override settings.
   */
  get techSettings() {
    const requiresNativeOverride = isMobileJS.android.device || isBrowserEdge(window.navigator);

    if (!requiresNativeOverride) {
      return {};
    }

    return {
      html5: {
        hls: {
          overrideNative: true,
        },
        nativeVideoTracks: false,
        nativeAudioTracks: false,
        nativeTextTracks: false,
      },
    };
  }

  /**
   * @returns {string} Amazon bid slot ID.
   */
  get slotId() {
    return 'nowcast';
  }

  /**
   * @returns {boolean} use Ad Tag override.
   */
  get hasAdTagOverride() {
    return typeof window.LUMIERE_NOWCAST_PREROLL_TAG !== 'undefined';
  }

  /**
   * @returns {boolean} use Ad Tag override.
   */
  get adTagOverride() {
    return window.LUMIERE_NOWCAST_PREROLL_TAG;
  }
}



// WEBPACK FOOTER //
// ./src/NowcastEmbed.js